import { Badge, badgeVariants } from "components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { uniqBy } from "lodash";
import { ComponentProps, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { SearchIcon, X } from "lucide-react";

import { ChevronDownIcon } from "@/assets";
import { ScrollInfinite } from "@/components/ScrollInfinite";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export type OptionData<T> = {
  value: T;
  label: string;
};

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

export type MultiSelectDynamicProps<T = string> = {
  onChangeValues: (values: OptionData<T>[]) => void;
  badgeVariant?: BadgeVariant;
  options: OptionData<T>[];
  onInputValueChange?: (inputValue: string) => void;
  classNamePopover?: string;
  hasMore?: boolean;
  loadMoreItems: () => void;
  isLoading?: boolean;
  selectedItems: OptionData<T>[];
} & Omit<ComponentProps<"input">, "onChange" | "onKeyDown" | "value" | "size"> &
  Pick<VariantProps<typeof buttonVariants>, "variant">;

const DEFAULT_SELECTED_ITEMS: OptionData<string>[] = [];

export const MultiSelectDynamic = <T extends string>({
  onChangeValues,
  badgeVariant = "default",
  className,
  options,
  onInputValueChange,
  classNamePopover,
  variant = "outline",
  hasMore,
  loadMoreItems,
  isLoading,
  selectedItems = DEFAULT_SELECTED_ITEMS as OptionData<T>[],
  ...inputProps
}: MultiSelectDynamicProps<T>) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const onInputChange = (value: string) => {
    setInputValue(value);
    if (onInputValueChange) {
      onInputValueChange(value.trim());
    }
  };

  // Remove a single value
  const removeSelectedItem = (value: string) => {
    const newValues = selectedItems.filter((item) => item.value !== value);
    onChangeValues(newValues);
  };

  // select an item
  const selectItem = (value: OptionData<T>) => {
    onChangeValues(uniqBy([...selectedItems, value], "value"));
  };
  // Clear all selected values
  const clearValues = () => {
    onChangeValues([]);
  };

  return (
    <div className="w-full max-w-sm">
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1 overflow-x-auto max-w-full mb-1">
          {selectedItems.map((item, index) => {
            return (
              <Badge
                key={`selectedItem-${item.value}-${index}`}
                variant={badgeVariant}
                className="max-w-full break-words whitespace-normal"
              >
                {item.label}
                <button
                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      removeSelectedItem(item.value);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => removeSelectedItem(item.value)}
                  type="button"
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-red-500" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button variant={variant} aria-expanded={open} className={cn("w-full justify-between", className)}>
            {inputProps.placeholder}
            <ChevronDownIcon
              className={`${open && "rotate-180 transform"} transition duration-150 ease-in-out w-5`}
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn("w-full", classNamePopover)}>
          <Command>
            <div className="flex h-9 items-center gap-2 border-b px-3">
              <SearchIcon className="size-4 shrink-0 opacity-50" />
              <input
                className="placeholder:text-muted-foreground flex h-10 w-full bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={inputProps.placeholder}
                onChange={(e) => onInputChange(e.target.value)}
                value={inputValue}
              />
            </div>
            <CommandEmpty>No record found.</CommandEmpty>
            <CommandGroup>
              <ScrollInfinite onLoadMore={loadMoreItems} hasNextPage={hasMore} isLoading={isLoading} allRows={options}>
                {options?.map((item, idx) => (
                  <CommandItem
                    key={`suggestion-${item.value}-${idx}`}
                    onSelect={() => selectItem(item)}
                    className="cursor-pointer"
                  >
                    <span>{item.label}</span>
                  </CommandItem>
                ))}
              </ScrollInfinite>
            </CommandGroup>
            {selectedItems.length > 0 && (
              <div className="border-t p-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    clearValues();
                    setOpen(false);
                  }}
                >
                  Clear selections
                </Button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
